// Early-finish LiveCook re-base (Option A — DECISIONS §H.2).
//
// When prep is confirmed complete before its anchored window ends, LiveCook
// should begin at the actual transition instant rather than idle until the
// originally-anchored liveStart (= serve − liveCook.nominalDuration). These
// pure helpers compute the unused-prep shift and slide ONLY the LiveCook
// schedule entries earlier by it — a display/classification overlay; the
// stored plan and the signed wire schedule are never mutated (mirroring the
// derived-prep lead overlay in derivePrep.ts).
//
// All arithmetic is in the server clock frame: the anchored `startAt`s are
// server-anchored ISO instants, and the transition epoch (`liveCookStartedAt`,
// a client `Date.now()`) is lifted into that frame with `clockOffset`
// (server − client) so the re-based phase lines up with the corrected wall
// clock the dials and classifier read.

import type { ScheduleEntry } from '@cookchow/shared';

/**
 * Milliseconds to slide the LiveCook phase earlier so it starts at the
 * transition instant. 0 when LiveCook starts on time or late (the late /
 * overdue behaviour is left exactly as it was). Defensive 0 on bad inputs.
 *
 * @param scheduledLiveStartIso anchored `phases.liveCook.startAt`
 * @param liveCookStartedAt     client epoch at the prep→LiveCook transition
 * @param clockOffset           server − client offset (ms)
 */
export function liveCookStartShiftMs(
  scheduledLiveStartIso: string | undefined,
  liveCookStartedAt: number | null,
  clockOffset: number,
): number {
  if (liveCookStartedAt == null || !scheduledLiveStartIso) return 0;
  const scheduledLiveStart = Date.parse(scheduledLiveStartIso);
  if (!Number.isFinite(scheduledLiveStart)) return 0;
  return Math.max(0, scheduledLiveStart - (liveCookStartedAt + clockOffset));
}

/**
 * Return a copy of `schedule` with every `liveCook` entry's `startAt` and
 * `mustCompleteBy` moved earlier by `shiftMs`. Other phases are untouched.
 * Returns the same array reference when there is nothing to shift so callers
 * keep referential-equality memoisation.
 */
export function rebaseLiveCook(
  schedule: readonly ScheduleEntry[],
  shiftMs: number,
): readonly ScheduleEntry[] {
  if (shiftMs <= 0) return schedule;
  return schedule.map((e) =>
    e.phase === 'liveCook'
      ? {
          ...e,
          startAt: new Date(Date.parse(e.startAt) - shiftMs).toISOString(),
          mustCompleteBy: new Date(Date.parse(e.mustCompleteBy) - shiftMs).toISOString(),
        }
      : e,
  );
}
